import { createSlice } from "@reduxjs/toolkit";
import { pathToTabName } from "../../utils/strings";
import isEmpty from "lodash/isEmpty";
import qs from "qs";
import api from "../api";
import { deAuthenticateUser } from "./userSlice";

const initialState = {};

export const resourceSlice = createSlice({
  name: "resource",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetAPI: (state, { payload }) => {
      state = {};
      return state;
    },
    handleError: (state, { payload }) => {
      const { error, path } = payload;
      state.error = error;
      state[`${path}Status`] = "failed";
    },
    handlePending: (state, { payload }) => {
      const { path } = payload;
      state[`${path}Status`] = "pending";
    },
    postSuccess: (state, { payload }) => {
      const { data, path } = payload;

      if (path === "line-items") return;

      if (state[path] && state[path].results) {
        state[path].results = [...state[path].results, data];
      } else {
        let convertedPath = pathToTabName[path] || path;
        state[convertedPath] = state[convertedPath]
          ? [...state[convertedPath], data]
          : [data];
      }
    },
    getSuccess: (state, { payload }) => {
      const { data, path, originalPath } = payload;
      let convertedPath = pathToTabName[path] || path;
      state[`${originalPath}Status`] = "done";

      if (
        path === "search-details" ||
        path === "search" ||
        path === "client-search-details" ||
        path === "vendor-invoice-search"
      ) {
        const valuesArray = Object.values(data);
        const keysArray = Object.keys(data);

        keysArray.forEach((key, idx) => {
          state[key] = valuesArray[idx];
        });

        if (path === "vendor-invoice-search") {
          state["vendor_invoices_payments"] = data.vendor_invoices
            .map((vi) => vi.vendor_invoice_payments)
            .flat();
        }
      } else {
        state[convertedPath] = data;
      }
    },
    updateSuccess: (state, { payload }) => {
      const { id, data, path } = payload;
      if (state[path] && state[path].results) {
        state[path].results = state[path].results.map((stateElement) =>
          stateElement.id === id || stateElement.stage === id
            ? data
            : stateElement
        );
      } else {
        const convertedPath = pathToTabName[path] || path;

        if (state[convertedPath]) {
          state[convertedPath] = state[convertedPath].map((stateElement) =>
            stateElement.id === id ? data : stateElement
          );
        }
      }
    },
    deleteSuccess: (state, { payload }) => {
      const { id, path } = payload;

      if (state[path] && state[path].results) {
        state[path].results = state[path].results.filter(
          (stateElement) => stateElement.id !== id
        );
      } else {
        const convertedPath = pathToTabName[path] || path;

        if (state[convertedPath])
          state[convertedPath] = state[convertedPath].filter(
            (stateElement) => stateElement.id !== id
          );
      }
    },
    clearResources: () => initialState,
  },
});

export const {
  handleError,
  getSuccess,
  updateSuccess,
  deleteSuccess,
  postSuccess,
  handlePending,
  clearResources,
  resetAPI,
} = resourceSlice.actions;

export const postResource =
  ({ path, data }) =>
  async (dispatch) => {
    try {
      const response = await api.post(`resource/${path}/`, data);

      return dispatch(postSuccess({ data: response.data, path }));
    } catch (error) {
      if (
        error &&
        error.response &&
        (error.response.status === 403 || error.response.status === 401)
      )
        dispatch(deAuthenticateUser());
      else return dispatch(handleError({ error: error.message, path }));
    }
  };

export const getResourceAPI =
  ({ path, query, reduxPath }) =>
  async (dispatch) => {
    if (query) {
      Object.keys(query).map((key) =>
        query[key] === null || query[key] === undefined || query[key] === ""
          ? delete query[key]
          : {}
      );
    }

    try {
      dispatch(handlePending({ path }));

      const response = await api.get(
        `resource/${path}/${!isEmpty(query) ? `?${qs.stringify(query)}` : ""}`
      );

      return dispatch(
        getSuccess({
          data: response.data,
          path: reduxPath || path,
          originalPath: path,
        })
      );
    } catch (error) {
      if (
        error &&
        error.response &&
        (error.response.status === 403 || error.response.status === 401)
      )
        dispatch(deAuthenticateUser());
      else return dispatch(handleError({ error: error.message, path }));
    }
  };

export const updateResource =
  ({ path, id, data }) =>
  async (dispatch) => {
    try {
      const response = await api.patch(`resource/${path}/${id}/`, data);

      return dispatch(updateSuccess({ path, id, data: response.data }));
    } catch (error) {
      if (
        error &&
        error.response &&
        (error.response.status === 403 || error.response.status === 401)
      )
        dispatch(deAuthenticateUser());
      else return dispatch(handleError({ error: error.message, path }));
    }
  };

export const deleteResource =
  ({ path, id }) =>
  async (dispatch) => {
    try {
      await api.delete(`resource/${path}/${id}`);

      return dispatch(deleteSuccess({ path, id }));
    } catch (error) {
      if (
        error &&
        error.response &&
        (error.response.status === 403 || error.response.status === 401)
      )
        dispatch(deAuthenticateUser());
      else return dispatch(handleError({ error: error.message, path }));
    }
  };

export const clearRedux = () => (dispatch) => {
  return dispatch(clearResources({}));
};

export const getResourceState = (state, path) => state.resource[path] || {};

// Loading state should usually be stored as an enum, like 'idle' | 'pending' | 'done' | 'failed'
export const getResourceStatus = (state, name) =>
  state.resource[name] || "idle";

export const setResourceStatus = (state, { path, status }) => {
  state.resource[path] = status;
};

export default resourceSlice.reducer;
